/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import MediaTypes from '@linc-technologies/linced-common/enums/media-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import SharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import { alias } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';
import { getKeyAndSourceFromUpload } from '@linc-technologies/linced-common/utils/uppy';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { isMobile } from '@linc-technologies/linced-common/utils/platform';
import { tagName } from '@ember-decorators/component';

@tagName('')
export default class ProfileBanner extends Component {
  @tracked
  navCardOpen;

  @action
  setNavCardOpen(value) {
    this.navCardOpen = value;
  }

  PersonType = PersonType;

  Jurisdiction = Jurisdiction;

  Scopes = Scopes;

  badges = [];

  // Services
  @service
  features;

  @service
  paperToaster;

  @service
  permissions;

  @service
  roles;

  @service
  session;

  @service
  store;

  maxBadges = 10;

  @tracked
  selectedBadge = null;

  @tracked
  badgeDialogOpen = false;

  @tracked
  showAllBadges = false;

  @tracked
  dataCardDialogOpen = false;

  @tracked
  isMobile = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  hideImage;

  @tracked
  isSticky;

  @tracked
  uploading;

  // Methods
  // isCaregiver checks if the provided user record is a caregiver.
  @computed('user.type')
  get isCaregiver() {
    return this.get('user.type') === PersonType.CAREGIVER;
  }

  /**
   * canSeeInfo computes whether the current user is able to see the info icon.
   */
  @computed('user.{id,type}')
  get canSeeInfo() {
    let user = this.user;
    if (this.session.personId === user.id) {
      return true;
    }

    switch (user.type) {
      case PersonType.LINC_ED:
        return false;

      case PersonType.STAFF:
        return this.permissions.meet(Scopes.TENANT_ADMIN);
    }

    return !this.roles.currentUserIsRestricted;
  }

  /**
   * genderClass returns a CSS class based on the user's set gender.
   */
  @computed('user.gender')
  get genderClass() {
    switch (this.get('user.gender')) {
      case 'M':
        return 'gender-male';
      case 'F':
        return 'gender-female';
    }
    return 'gender-neutral';
  }

  @computed('badges.[]')
  get displayBadges() {
    return this.allBadgesSorted.slice(0, this.maxBadges);
  }

  @computed('badges.[]')
  get allBadgesSorted() {
    return sortBy(this.badges, 'createTime').reverse();
  }

  @computed('badges.[]')
  get showMore() {
    return this.badges.length > this.maxBadges;
  }

  init() {
    super.init(...arguments);
    this.isMobile = isMobile();
  }

  /**
   * startUpload provides the callback for when a profile picture has started
   * being uploaded.
   */
  @action
  startUpload() {
    this.uploading = true;
  }

  @action
  toggleSticky(isSticky) {
    this.isSticky = isSticky;
  }

  /**
   * handleUpload provides the callback for handling what to do after a
   * profile picture has been uploaded.
   *
   * @param file
   * @param upload
   */
  @action
  handleUpload(file, upload) {
    let { key, mediaSource } = getKeyAndSourceFromUpload(upload);
    let person = this.user;

    let sharedWith = SharingLevel.SCHOOL;

    if (!isEmpty(person.get('caregiver'))) {
      sharedWith = SharingLevel.PUBLIC;
    }

    this.store
      .createRecord('media', {
        schoolId: this.get('session.school'),
        personId: this.get('session.person'),
        sharedWith,
        mediaType: MediaTypes.IMAGE,
        mediaSource: mediaSource,
        mimeType: file.type,
        fileName: file.name,
        storageKey: key,
        fileSize: file.size,
      })
      .save()
      .then((media) => {
        person.set('profileMedia', media.id);
        return person.save();
      })
      .catch(() => {
        person.rollbackAttributes(); // Revert unsaved changes, i.e. the profileMediaId
        this.paperToaster.show(`Error saving profile image!`, {
          duration: 8000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .finally(() => {
        this.uploading = false;
        this.hideImage = false;
      });
  }

  /**
   * handleUploadError provides the callback to deal with upload errors.
   */
  @action
  handleUploadError() {
    // The upload failed before `handleUpload` ran, so clear the spinner here
    // too or it spins forever.
    this.uploading = false;
    this.paperToaster.show(`Error saving profile image!`, {
      duration: 8000,
      position: 'top right',
      toastClass: 'warning',
    });
  }

  @action
  openBadgeDialog(badge) {
    this.selectedBadge = badge;
    this.showAllBadges = false;
    this.badgeDialogOpen = true;
  }

  @action
  closeBadgeDialog() {
    this.badgeDialogOpen = false;
    this.selectedBadge = null;
  }

  @action
  openDataCardDialog() {
    this.dataCardDialogOpen = true;
  }

  @action
  closeDataCardDialog() {
    this.dataCardDialogOpen = false;
  }
}
